import { isDefinitionSet } from "../types/requests.js";
import { ClientIds } from "@gsb-core/client/internal";
/**
 * Utility functions for working with GSB entities
 */
/**
 * Standard GSB date field names
 */
export const GSB_DATE_FIELDS = {
    CREATE_DATE: "createDate",
    LAST_UPDATE_DATE: "lastUpdateDate",
    CREATED_BY_ID: "createdBy_id",
    LAST_UPDATED_BY_ID: "lastUpdatedBy_id",
};
function isRecord(value) {
    return typeof value === "object" && value !== null;
}
/**
 * Gets the correct sorting configuration for GSB entities
 * @param sortByCreateDate Whether to sort by creation date (true) or last update date (false)
 * @param ascending Whether to sort in ascending order
 * @returns The sort columns configuration for GSB queries
 */
export function getGsbDateSortCols(sortByCreateDate = false, ascending = false) {
    return [
        {
            col: {
                name: sortByCreateDate
                    ? GSB_DATE_FIELDS.CREATE_DATE
                    : GSB_DATE_FIELDS.LAST_UPDATE_DATE,
            },
            sortType: ascending ? "ASC" : "DESC",
        },
    ];
}
/**
 * Compares two objects for equality, ignoring specified properties
 * @param obj1 First object to compare
 * @param obj2 Second object to compare
 * @param ignoredProps Array of property names to ignore during comparison
 * @returns True if objects are functionally equal (ignoring specified properties)
 */
export function compareObjects(obj1, obj2, ignoredProps = []) {
    if (obj1 === obj2)
        return true;
    if (!isRecord(obj1) || !isRecord(obj2)) {
        return false;
    }
    const record1 = obj1;
    const record2 = obj2;
    // Get all unique keys from both objects
    const allKeys = [
        ...new Set([...Object.keys(record1), ...Object.keys(record2)]),
    ];
    for (const key of allKeys) {
        // Skip ignored properties
        if (ignoredProps.includes(key))
            continue;
        // Skip if both values are undefined
        const value1 = record1[key];
        const value2 = record2[key];
        if (value1 === undefined && value2 === undefined)
            continue;
        // Check if one has the property and the other doesn't
        if ((value1 === undefined) !== (value2 === undefined))
            return false;
        // Check for nested objects (not including Arrays)
        if (typeof value1 === "object" &&
            typeof value2 === "object" &&
            value1 !== null &&
            value2 !== null &&
            !Array.isArray(value1) &&
            !Array.isArray(value2)) {
            if (!compareObjects(value1, value2, ignoredProps))
                return false;
            continue;
        }
        // Check for array equality
        if (Array.isArray(value1) && Array.isArray(value2)) {
            if (value1.length !== value2.length)
                return false;
            // For simple arrays of primitives
            if (value1.every((item) => typeof item !== "object" || item === null) &&
                value2.every((item) => typeof item !== "object" || item === null)) {
                // Sort and join for comparison if array contains only primitives
                const sorted1 = value1.map(String).sort().join(",");
                const sorted2 = value2.map(String).sort().join(",");
                if (sorted1 !== sorted2)
                    return false;
            }
            else {
                // For arrays of objects, we need to compare each item
                // This is a simplification; for complex nested arrays a more sophisticated
                // comparison would be needed
                for (let i = 0; i < value1.length; i++) {
                    if (!compareObjects(value1[i], value2[i], ignoredProps))
                        return false;
                }
            }
            continue;
        }
        // Handle dates
        if (value1 instanceof Date && value2 instanceof Date) {
            if (value1.getTime() !== value2.getTime())
                return false;
            continue;
        }
        // Simple value comparison
        if (value1 !== value2)
            return false;
    }
    return true;
}
/**
 * Utility class for GSB operations
 */
export class GsbUtils {
    static StringPropertyDefinitionId = "c6c34bf3-f51b-4e69-a689-b09847be74b9";
    static NumberPropertyDefinitionId = "35efcf9c-fff0-44d4-8972-73a9a32b93fa";
    static BooleanPropertyDefinitionId = "7868afdf-2709-45be-87e3-87de8d35f30f";
    static DatePropertyDefinitionId = "12e647e0-ebd2-4ec2-a4e3-82c1dfe07da2";
    static ReferencePropertyDefinitionId = "924acba8-58c5-4881-940d-472ec01eba5f";
    static EnumPropertyDefinitionId = "7bf08f4f-7de0-469e-bbfb-f4c43762f4d7";
    static IDPropertyDefinitionId = "5c0aa76f-9c32-4e7e-a4bc-b56e93877883";
    static RichTextPropertyDefinitionId = "e07f578e-2705-49c1-b97f-3ca5963c67c0";
    static HtmlPropertyDefinitionId = "e0e03719-ee27-41b1-9452-d441c291fa5e";
    static EmailPropertyDefinitionId = "df7ce94b-d59c-4b67-8519-aa4c98ab477c";
    static PasswordPropertyDefinitionId = "7291fbc2-a7cf-4713-a876-0cff085cc035";
    static newId() {
        return ClientIds.newId();
    }
    /**
     * Checks if an ID is empty (null, undefined, or empty GUID)
     * @param id The ID to check
     * @returns true if the ID is empty, false otherwise
     */
    static isIdEmpty(id) {
        if (!id)
            return true;
        // Check for empty GUID (all zeros)
        const emptyGuidRegex = /^[0-]{36}$/;
        return emptyGuidRegex.test(id);
    }
    /**
     * Checks if an ID is valid (not empty and not an empty GUID)
     * @param id The ID to check
     * @returns true if the ID is valid, false otherwise
     */
    static isValidId(id) {
        return !this.isIdEmpty(id);
    }
    /**
     * Checks if an array of IDs contains any empty IDs
     * @param ids Array of IDs to check
     * @returns true if any ID is empty, false otherwise
     */
    static hasEmptyIds(ids) {
        return ids.some((id) => this.isIdEmpty(id));
    }
    /**
     * Filters out empty IDs from an array
     * @param ids Array of IDs to filter
     * @returns Array of valid IDs
     */
    static filterEmptyIds(ids) {
        return ids.filter((id) => this.isValidId(id));
    }
    /**
     * Gets the view form mode from the given property
     * @param prop The property to get the view form mode from
     * @returns The view form mode or undefined if not found
     */
    static getViewFormMode(prop) {
        if (!prop)
            return undefined;
        if (typeof prop.viewFormMode === "number") {
            return prop.viewFormMode;
        }
        if (typeof prop.formModes === "number") {
            return (prop.formModes >> 8) & 0xff;
        }
        return undefined;
    }
    /**
     * Gets the create form mode from the given property
     * @param prop The property to get the create form mode from
     * @returns The create form mode or undefined if not found
     */
    static getCreateFormMode(prop) {
        if (!prop)
            return undefined;
        if (typeof prop.createFormMode === "number") {
            return prop.createFormMode;
        }
        if (typeof prop.formModes === "number") {
            return (prop.formModes >> 16) & 0xff;
        }
        return undefined;
    }
    /**
     * Gets the update form mode from the given property
     * @param prop The property to get the update form mode from
     * @returns The update form mode or undefined if not found
     */
    static getUpdateFormMode(prop) {
        if (!prop)
            return undefined;
        if (typeof prop.updateFormMode === "number") {
            return prop.updateFormMode;
        }
        if (typeof prop.formModes === "number") {
            return prop.formModes & 0xff;
        }
        return undefined;
    }
    /**
     * Converts a string to PascalCase
     * @param text The string to convert
     * @returns The string in PascalCase format
     */
    static toPascalCase(text) {
        if (!text)
            return "";
        // Remove special characters, convert spaces to single space
        const cleanedText = text.replace(/[^a-zA-Z0-9\s]/g, "").trim();
        // Convert to PascalCase
        return cleanedText
            .split(/\s+/)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join("");
    }
    /**
     * Converts a string to camelCase
     * @param title The string to convert
     * @returns The string in camelCase format
     */
    static toCamelCase(title) {
        if (!title)
            return undefined;
        return title
            .replace(/\s+/g, "")
            .replace(/\b\w/g, (char) => char.toUpperCase());
    }
    /**
     * Checks if the request object contains an entity definition
     * @param req The request object to check
     * @returns true if the request object contains an entity definition, false otherwise
     */
    static hasEntityDef(req) {
        return isDefinitionSet(req);
    }
}
//# sourceMappingURL=gsb-utils.js.map